/**
 * "Send by Netlise" composer (README E): channel rows, template, language,
 * message, attach PDF, the footer cost line and Send. Used by the share
 * dialog for an issued document and, in "pre-issue" mode, by the editor's
 * "Issue and send", where texts are rendered locally because the document
 * has no number or link yet. Rules live in composerModel.ts.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InvoiceRepository, { Credits, SendRequest, SendResult } from '../../../internals/repository/InvoiceRepository';
import {
  buildSendRequest, Channel, CHANNEL_META, channelAvailability, channelCostLabel, CHANNELS, costLine, defaultTicks,
  IdemState, idempotencyFor, Lang, Reason, recipientProblems, RecipientProblems, requestSignature, SendFailure,
  sendFailure, TEMPLATE_OPTIONS, usableTicks,
} from '../composerModel';
import { errorText } from '../detailModel';
import { smsSegments, SmsInfo, toE164 } from '../shareLinks';

export interface ComposerText {
  text: string;
  subject?: string;
  templateKey: string;
  language: Lang;
  link?: string;
  to?: { phoneE164?: string; email?: string };
  sms?: { segments: number };
}
export type TextSource = (q: { channel: Channel; language?: Lang; templateKey?: string }) => Promise<ComposerText>;

export type PreviewKind = 'wa' | 'sms' | 'email';
export interface ComposerPreview {
  pv: PreviewKind;
  setPv: (p: PreviewKind) => void;
  msg: string;
  link: string;
  sms: SmsInfo;
  subject?: string;
  emailTo: string;
}

const LANGS: { value: Lang; label: string }[] = [
  { value: 'en', label: 'English' }, { value: 'si', label: 'සිංහල' }, { value: 'ta', label: 'தமிழ்' },
];
const PV_OF: Record<Channel, PreviewKind> = { WHATSAPP: 'wa', SMS: 'sms', EMAIL: 'email' };
const newIdem = () => `snd-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-text2';

export type CreditsState = { credits: Credits | null; state: 'loading' | 'ready' | 'error' };

/** Credits and which channels Netlise can send on; loaded when a composer opens. */
export function useCredits(shopId?: string): CreditsState {
  const [s, setS] = useState<CreditsState>({ credits: null, state: 'loading' });
  useEffect(() => {
    if (!shopId) return undefined;
    let live = true;
    InvoiceRepository.credits(shopId)
      .then((c) => { if (live) setS({ credits: c, state: 'ready' }); })
      .catch(() => { if (live) setS({ credits: null, state: 'error' }); });
    return () => { live = false; };
  }, [shopId]);
  return s;
}

/**
 * A toast on document.body, so it outlives the dialog or page that raised it
 * (the composer closes on success; "Issue and send" navigates away).
 */
export function flashToast(text: string, tone: 'ok' | 'warn' = 'ok') {
  document.querySelectorAll('[data-nl-flash]').forEach((n) => n.remove());
  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  el.setAttribute('data-nl-flash', '');
  el.className = 'fixed bottom-6 left-1/2 z-[1100] flex max-w-[calc(100vw-32px)] -translate-x-1/2 items-center gap-2 rounded-[10px] px-4 py-2.5 text-[14px] font-medium text-white';
  el.style.background = '#131318';
  el.style.boxShadow = 'var(--shadowLg)';
  const icon = document.createElement('span');
  icon.setAttribute('aria-hidden', 'true');
  icon.style.color = tone === 'ok' ? '#45D39F' : '#F0AE4E';
  icon.textContent = tone === 'ok' ? '✓' : '!';
  const label = document.createElement('span');
  label.textContent = text;
  el.append(icon, label);
  document.body.appendChild(el);
  setTimeout(() => el.remove(), tone === 'ok' ? 3000 : 6000);
}

export default function MessageComposer({
  mode = 'send', source, customer, credits, defaultChannels, unfillVars, measure,
  submit, onSent, onCancel, onSwitchToDevice, onBusyChange, renderPreview, wide = true,
}: {
  mode?: 'send' | 'pre-issue';
  source: TextSource;
  /** Contacts on file, for "No phone number on file" and the recipients' starting values. */
  customer: { phone?: string | null; email?: string | null };
  credits: CreditsState;
  defaultChannels?: Channel[] | null;
  /** Pre-issue: shown values that go back to {variables} in customText. */
  unfillVars?: (lang: Lang) => Record<string, string>;
  /** Pre-issue: the text with placeholders at their real length, for SMS segments. */
  measure?: (text: string) => string;
  submit: (req: SendRequest, idempotencyKey: string) => Promise<SendResult | void>;
  onSent: (result: SendResult | undefined, req: SendRequest) => void;
  onCancel: () => void;
  onSwitchToDevice?: () => void;
  onBusyChange?: (busy: boolean) => void;
  renderPreview?: (p: ComposerPreview) => React.ReactNode;
  wide?: boolean;
}) {
  const preIssue = mode === 'pre-issue';
  const [lang, setLang] = useState<Lang | null>(null);
  const [tpl, setTpl] = useState<string | null>(null);
  const [texts, setTexts] = useState<Partial<Record<Channel, ComposerText>>>({});
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [phone, setPhone] = useState(customer.phone ? toE164(customer.phone) || customer.phone : '');
  const [email, setEmail] = useState(customer.email || '');
  const [ticked, setTicked] = useState<Channel[]>([]);
  const [attachPdf, setAttachPdf] = useState(true);
  const [server, setServer] = useState<Partial<Record<Channel, Reason>>>({});
  const [serverTo, setServerTo] = useState<RecipientProblems>({});
  const [banner, setBanner] = useState<SendFailure | null>(null);
  const [sending, setSending] = useState(false);
  const [available, setAvailable] = useState<number | null>(null);
  const [pv, setPv] = useState<PreviewKind>('wa');
  const [touchedFields, setTouchedFields] = useState(false);
  const idem = useRef<IdemState | null>(null);
  const ticksSet = useRef(false);
  const touchedTo = useRef(false);
  const req = useRef(0);
  const sourceRef = useRef(source);
  sourceRef.current = source;

  const load = useCallback(async (language?: Lang, templateKey?: string) => {
    const id = ++req.current;
    setLoading(true);
    setLoadError(null);
    try {
      const wa = await sourceRef.current({ channel: 'WHATSAPP', language, templateKey });
      if (id !== req.current) return;
      setTexts({ WHATSAPP: wa });
      setText(wa.text);
      setLang(wa.language);
      setTpl(wa.templateKey);
      if (!touchedTo.current) {
        setPhone((p) => p || wa.to?.phoneE164 || '');
        setEmail((e) => e || wa.to?.email || '');
      }
      setLoading(false);
      const q = { language: wa.language, templateKey: wa.templateKey };
      const [sms, mail] = await Promise.all([
        sourceRef.current({ channel: 'SMS', ...q }).catch(() => undefined),
        sourceRef.current({ channel: 'EMAIL', ...q }).catch(() => undefined),
      ]);
      if (id === req.current) setTexts({ WHATSAPP: wa, SMS: sms, EMAIL: mail });
    } catch (e) {
      if (id !== req.current) return;
      setLoadError(errorText(e, { invalid_state: 'Issue the document before sending it.' }));
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const configured = credits.credits?.channels ?? null;
  const onFile = useMemo(() => ({
    phone: customer.phone || texts.WHATSAPP?.to?.phoneE164,
    email: customer.email || texts.WHATSAPP?.to?.email,
  }), [customer.phone, customer.email, texts.WHATSAPP]);
  const avail = useMemo(() => channelAvailability({ customer: onFile, configured, server }), [onFile, configured, server]);
  const noneAvailable = CHANNELS.every((c) => !avail[c].available);

  // Default ticks once both the credits and the first text have answered.
  useEffect(() => {
    if (ticksSet.current || credits.state === 'loading' || (!texts.WHATSAPP && !loadError)) return;
    ticksSet.current = true;
    const t = defaultTicks(defaultChannels, avail);
    setTicked(t);
    if (t.length) setPv(PV_OF[t[0]]);
  }, [credits.state, texts.WHATSAPP, loadError, defaultChannels, avail]);

  useEffect(() => { onBusyChange?.(sending); }, [sending, onBusyChange]);

  const base = texts.WHATSAPP;
  const edited = !!base && text !== base.text;
  const sel = usableTicks(ticked, avail);
  const smsText = edited ? text : texts.SMS?.text ?? text;
  const smsInfo = smsSegments(measure ? measure(smsText) : smsText);
  const smsSegs = !edited && !measure && texts.SMS?.sms?.segments ? texts.SMS.sms.segments : smsInfo.segments;
  const balance = available ?? credits.credits?.available ?? null;
  const cost = costLine(sel, smsSegs, credits.credits?.costs, credits.state === 'ready' || available != null ? balance : null);
  const localTo = recipientProblems({ phone, email }, sel);
  const toProblems: RecipientProblems = { phone: serverTo.phone || localTo.phone, email: serverTo.email || localTo.email };
  const hasToProblem = !!(toProblems.phone || toProblems.email);

  const request = useMemo(() => buildSendRequest({
    ticked: sel, to: { phone, email }, templateKey: tpl, language: lang, text, baseText: base?.text ?? text, attachPdf,
    variables: unfillVars && lang ? unfillVars(lang) : undefined,
  }), [sel.join(','), phone, email, tpl, lang, text, base, attachPdf, unfillVars]); // eslint-disable-line react-hooks/exhaustive-deps

  const blocked = sending || loading || !base || cost.none || cost.short || noneAvailable || hasToProblem;
  const retrySame = !!banner?.retry && !!idem.current && idem.current.sig === requestSignature(request);
  const sendLabel = sending
    ? (preIssue ? 'Issuing…' : 'Sending…')
    : retrySame ? 'Try again' : preIssue ? 'Issue and send' : 'Send';

  const toggle = (ch: Channel) => {
    if (!avail[ch].available) return;
    const on = ticked.includes(ch);
    setTicked((t) => (on ? t.filter((x) => x !== ch) : [...t, ch]));
    if (!on) setPv(PV_OF[ch]);
  };

  const send = async () => {
    setTouchedFields(true);
    if (blocked) return;
    idem.current = idempotencyFor(idem.current, request, newIdem);
    setSending(true);
    setBanner(null);
    try {
      const result = await submit(request, idem.current.key);
      idem.current = null;
      setSending(false);
      onSent(result || undefined, request);
    } catch (e) {
      const f = sendFailure(e, { preIssue });
      setBanner(f);
      if (f.server) {
        setServer((s) => ({ ...s, ...f.server }));
        const off = Object.keys(f.server) as Channel[];
        setTicked((t) => t.filter((c) => !off.includes(c)));
      }
      if (f.recipient) setServerTo((s) => ({ ...s, ...f.recipient }));
      if (f.available != null) setAvailable(f.available);
      setSending(false);
    }
  };

  const link = base?.link || '';
  const previewMsg = pv === 'wa' ? base?.text ?? text : pv === 'sms' ? smsText : edited ? text : texts.EMAIL?.text ?? text;
  const subject = texts.EMAIL?.subject;
  const showPreview = !!renderPreview;
  const firstPhoneRow = sel.find((c) => c !== 'EMAIL');

  return (
    <>
      <div
        className={`grid ${showPreview ? 'min-h-[520px]' : ''}`}
        style={{ gridTemplateColumns: showPreview && wide ? 'minmax(0,1fr) 380px' : 'minmax(0,1fr)' }}
      >
        <div role={preIssue ? undefined : 'tabpanel'} className="flex min-w-0 flex-col gap-4 px-6 py-5">
          {banner && (
            <div role="alert" className="flex gap-2.5 rounded-[10px] border border-neg bg-neg-soft px-3.5 py-3">
              <span aria-hidden className="font-bold" style={{ color: 'var(--negFg)' }}>!</span>
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="font-semibold text-text1">{banner.title}</span>
                <span className="text-[13px] text-text2">{banner.text}</span>
              </div>
            </div>
          )}
          {loadError && (
            <div role="alert" className="rounded-lg border border-neg/30 bg-neg-soft px-3.5 py-2.5 text-[13px]" style={{ color: 'var(--negFg)' }}>
              {loadError}
              {' '}
              <button type="button" className="font-semibold underline" onClick={() => load(lang ?? undefined, tpl ?? undefined)}>Try again</button>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-[13px] font-semibold text-text2" id="composer-channels">Channels</span>
            <div role="group" aria-labelledby="composer-channels" className="flex flex-col gap-2">
              {CHANNELS.map((ch) => {
                const a = avail[ch];
                const on = a.available && ticked.includes(ch);
                const meta = CHANNEL_META[ch];
                const isPhone = ch !== 'EMAIL';
                const problem = on && (isPhone ? (ch === firstPhoneRow ? toProblems.phone : undefined) : toProblems.email);
                const showProblem = problem && (touchedFields || !!(isPhone ? serverTo.phone : serverTo.email) || !!(isPhone ? phone : email).trim());
                return (
                  <div
                    key={ch}
                    className={`grid items-center gap-2.5 rounded-[10px] border px-3 py-2.5 ${on ? 'border-brand bg-brand-soft' : 'border-borderc bg-surface'} ${a.available ? '' : 'opacity-60'}`}
                    style={{ gridTemplateColumns: '20px 26px minmax(0,1fr)' }}
                  >
                    <input
                      type="checkbox"
                      className="nl-check m-0"
                      checked={on}
                      disabled={!a.available || sending}
                      onChange={() => toggle(ch)}
                      aria-label={meta.name}
                    />
                    <span aria-hidden className="grid h-6 w-6 place-items-center rounded-[7px] text-[11px] font-bold text-white" style={{ background: meta.bg }}>{meta.glyph}</span>
                    <div className="flex min-w-0 flex-wrap items-center gap-2.5">
                      <span className="min-w-[72px] font-semibold text-text1">{meta.name}</span>
                      {a.available ? (
                        <input
                          className={`nl-input h-8 min-w-[160px] flex-1 font-mono text-[13px] ${showProblem ? '!border-neg' : ''}`}
                          aria-label={`${meta.name} recipient`}
                          aria-invalid={!!showProblem || undefined}
                          value={isPhone ? phone : email}
                          inputMode={isPhone ? 'tel' : 'email'}
                          placeholder={isPhone ? '077 123 4567' : 'name@example.com'}
                          disabled={sending}
                          onChange={(e) => {
                            touchedTo.current = true;
                            if (isPhone) { setPhone(e.target.value); setServerTo((s) => ({ ...s, phone: undefined })); }
                            else { setEmail(e.target.value); setServerTo((s) => ({ ...s, email: undefined })); }
                          }}
                          onBlur={() => { if (isPhone) { const n = toE164(phone); if (n) setPhone(n); } }}
                        />
                      ) : (
                        <span className="text-[12px] font-semibold text-text3">{a.text}</span>
                      )}
                      {a.available && (
                        <span className="ml-auto text-[12px] text-text3">{channelCostLabel(ch, smsSegs, credits.credits?.costs)}</span>
                      )}
                    </div>
                    {showProblem && (
                      <span className="col-start-3 text-[12px] font-semibold" style={{ color: 'var(--negFg)' }}>{problem}</span>
                    )}
                  </div>
                );
              })}
            </div>
            {noneAvailable && !loading && (
              <span className="rounded-[10px] bg-surface2 px-3 py-2.5 text-[13px] text-text2">
                No channel is available for this customer. Add a mobile number or email to their profile
                {onSwitchToDevice ? (
                  <>, or <button type="button" className="font-semibold text-brand underline" onClick={onSwitchToDevice}>share from your device</button>.</>
                ) : '.'}
              </span>
            )}
          </div>

          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}>
            <label className={labelCls}>
              Template
              <select
                className="nl-select"
                value={tpl ?? ''}
                disabled={!base || sending}
                onChange={(e) => load(lang ?? undefined, e.target.value)}
              >
                {!tpl && <option value="">Loading…</option>}
                {TEMPLATE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <div className={labelCls}>
              <span id="composer-lang">Language</span>
              <div role="radiogroup" aria-labelledby="composer-lang" className="flex gap-0.5 rounded-lg border border-borderc bg-surface2 p-[3px]">
                {LANGS.map((l) => {
                  const on = lang === l.value;
                  return (
                    <button
                      key={l.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      disabled={!base || sending}
                      onClick={() => { if (!on) load(l.value, tpl ?? undefined); }}
                      className={`h-[30px] flex-1 rounded-md text-[13px] font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${on ? 'bg-surface text-text1' : 'text-text2'}`}
                      style={on ? { boxShadow: '0 1px 2px rgba(0,0,0,.12)' } : undefined}
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <label className={labelCls}>
            <span className="flex justify-between gap-2.5">
              Message
              {edited && (
                <button type="button" className="text-[12px] font-semibold text-brand" onClick={() => setText(base!.text)}>
                  Reset to template
                </button>
              )}
            </span>
            <textarea
              className="nl-input !h-auto py-2.5 font-normal leading-normal"
              style={{ resize: 'vertical' }}
              rows={preIssue ? 4 : 5}
              value={!base ? '' : text}
              placeholder={loading ? 'Loading message…' : ''}
              disabled={!base || sending}
              onChange={(e) => setText(e.target.value)}
            />
            {sel.includes('WHATSAPP') && (
              <span className="text-[12px] font-normal text-text3">
                WhatsApp sends the approved template text
                {sel.some((c) => c !== 'WHATSAPP') ? '. Your edits apply to SMS and email.' : '.'}
              </span>
            )}
            {preIssue && (
              <span className="text-[12px] font-normal text-text3">
                The number and the link <span className="font-mono">i.netlise.com/…</span> are filled in on issue.
              </span>
            )}
          </label>

          {sel.includes('EMAIL') && subject && (
            <div className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-semibold text-text2">Email subject</span>
              <span className="truncate rounded-lg border border-borderc bg-surface2 px-3 py-2 text-text1" title={subject}>{subject}</span>
            </div>
          )}

          <label className={`flex items-center gap-2 text-[14px] font-medium text-text1 ${sel.includes('EMAIL') ? '' : 'opacity-50'}`}>
            <input
              type="checkbox"
              className="nl-check m-0"
              checked={attachPdf && sel.includes('EMAIL')}
              disabled={!sel.includes('EMAIL') || sending}
              onChange={() => setAttachPdf((v) => !v)}
            />
            Attach PDF to email
          </label>
        </div>

        {renderPreview?.({
          pv, setPv, msg: previewMsg, link, sms: smsSegments(previewMsg), subject, emailTo: email.trim() || '—',
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-t border-borderc px-6 py-3.5">
        <span
          aria-live="polite"
          className="min-w-[220px] flex-1 text-[13px] font-medium"
          style={{ color: cost.short ? 'var(--negFg)' : 'var(--text2)' }}
        >
          {credits.state === 'loading' ? 'Checking credits…' : cost.text}
          {cost.short && (onSwitchToDevice ? (
            <>
              {' '}
              <button type="button" className="font-semibold underline" onClick={onSwitchToDevice}>Share from your device instead.</button>
            </>
          ) : preIssue ? ' Issue without sending, then share from your device.' : null)}
        </span>
        <button type="button" className="nl-btn nl-btn--secondary" onClick={onCancel} disabled={sending}>Cancel</button>
        <button type="button" className="nl-btn nl-btn--primary min-w-[120px]" onClick={send} disabled={blocked} aria-busy={sending || undefined}>
          {sendLabel}
        </button>
      </div>
    </>
  );
}

/** Small dialog shell for the composer outside the share dialog (the editor's "Issue and send"). */
export function ComposerDialog({
  title, sub, onClose, busy, width = 680, children,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode;
  onClose: () => void;
  busy?: boolean;
  width?: number;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('button[aria-label="Close"]')?.focus();
    return () => back?.focus?.();
  }, []);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); if (!busy) onClose(); return; }
    if (e.key !== 'Tab' || !ref.current) return;
    const items = Array.from(ref.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));
    if (!items.length) return;
    const [first, last] = [items[0], items[items.length - 1]];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  return (
    <div className="nl-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby="composer-dialog-title" onKeyDown={onKey} className="nl-modal flex flex-col" style={{ maxWidth: width }}>
        <div className="flex items-start justify-between gap-3 px-6 pt-5">
          <div className="flex min-w-0 flex-col gap-[3px]">
            <h2 id="composer-dialog-title" className="m-0 text-[20px] font-semibold text-text1">{title}</h2>
            {sub && <p className="m-0 text-[13px] text-text2">{sub}</p>}
          </div>
          <button type="button" aria-label="Close" disabled={busy} className="nl-btn nl-btn--ghost nl-btn--sm !w-8 !px-0 text-[18px]" onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
